import { Injectable } from '@angular/core';

import { KnowledgeBaseArticle, ListRequest, ListResponse } from 'src/models';

import { ApiService } from './api.service';
import { GenericObjectsService } from './generic-objects.service';

@Injectable({
  providedIn: 'root',
})
export class KnowledgeBaseArticlesService extends GenericObjectsService<KnowledgeBaseArticle> {
  protected override baseUrl = '/knowledge-base/articles';

  constructor(apiService: ApiService) {
    super(apiService);
  }

  async listAllStubs(params: ListRequest = {}): Promise<Partial<KnowledgeBaseArticle>[]> {
    let fetchedArticles: Partial<KnowledgeBaseArticle>[] = [];
    let nextCursor: string | undefined = undefined;
    const limit = 100;
    let response: ListResponse<Partial<KnowledgeBaseArticle>>;

    // Ensure essential fields are selected, allow overriding/adding via params
    const baseFields = ['id', 'title', 'collectionId', 'status', 'updatedAt'];
    const finalFields = params.fields ? [...new Set([...baseFields, ...params.fields])] : baseFields;

    const baseParams: ListRequest = {
      ...params,
      fields: finalFields,
      limit: params.limit ?? limit,
    };
    delete baseParams.nextCursor;
    delete baseParams.previousCursor;

    let currentParams: ListRequest;
    let hasNextPage = true;

    do {
      try {
        currentParams = { ...baseParams };
        if (nextCursor) {
          currentParams.cursor = nextCursor;
        }

        response = await this.list(currentParams);

        if (response?.data) {
          fetchedArticles = fetchedArticles.concat(response.data);
        }

        nextCursor = response?.cursors?.next;
        hasNextPage = response?.cursors?.hasNext ?? false;

        if (!hasNextPage) {
          break;
        }
      } catch (listError) {
        console.error(
          `Error fetching article stubs page with cursor ${currentParams?.cursor}:`,
          listError,
          currentParams,
        );
        hasNextPage = false;
        throw listError;
      }
    } while (hasNextPage);

    return fetchedArticles;
  }

  async bulkDelete(articleIds: string[]): Promise<void> {
    await this.apiService.postPromise<void>(`${this.baseUrl}/bulk`, { articleIds });
  }

  async bulkMove(articleIds: string[], targetCollectionId: string | null): Promise<void> {
    const payload = {
      articleIds,
      targetCollectionId,
    };
    await this.apiService.postPromise<void>(`${this.baseUrl}/bulk-move`, payload);
  }

  /**
   * Fetches multiple articles by their IDs, optionally specifying which fields to retrieve.
   * Defaults to fetching 'id' and 'userGroupIds'.
   */
  async getArticlesByIds(
    articleIds: string[],
    fields: string[] = ['id', 'userGroupIds', 'channelIds'],
  ): Promise<Partial<KnowledgeBaseArticle>[]> {
    if (!articleIds || articleIds.length === 0) {
      return [];
    }
    const params: ListRequest = {
      fields: [...new Set(fields)],
      where: [
        {
          field: 'id',
          operator: 'in',
          value: articleIds,
        },
      ],
    };
    const response = await this.list(params);
    return response.data ?? [];
  }

  /**
   * Sets the userGroupIds for multiple articles, overwriting existing groups.
   */
  async bulkSetUserGroups(articleIds: string[], groupIds: string[]): Promise<void> {
    const payload = {
      articleIds,
      groupIds,
    };
    await this.apiService.postPromise<void>(`${this.baseUrl}/bulk-set-groups`, payload);
  }

  /**
   * Sets the channelIds for multiple articles, overwriting existing channels.
   */
  async bulkSetChannels(articleIds: string[], channelIds: string[]): Promise<void> {
    const payload = {
      articleIds,
      channelIds,
    };
    await this.apiService.postPromise<void>(`${this.baseUrl}/bulk-set-channels`, payload);
  }

  /**
   * Imports a document and creates a knowledge base article from it.
   * Supports 65+ file types including PDF, DOCX, PPTX, images, etc.
   * @param file The document file to import
   * @param options Import options (title, projectId, collectionId, etc.)
   * @returns Promise with the created article and metadata
   */
  async importDocument(
    file: File,
    options?: {
      title?: string;
      projectId?: string;
      collectionId?: string;
      userGroupIds?: string[];
      channelIds?: string[];
      extractImages?: boolean;
      strategy?: 'auto' | 'hi_res' | 'fast' | 'vlm' | 'ocr_only';
      vlmModelProvider?: 'anthropic' | 'openai' | 'bedrock' | 'vertex';
    },
  ): Promise<{
    article: KnowledgeBaseArticle;
    imageCount: number;
    created: boolean;
    sourceUrl: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    if (options?.title) {
      formData.append('title', options.title);
    }
    if (options?.projectId) {
      formData.append('projectId', options.projectId);
    }
    if (options?.collectionId) {
      formData.append('collectionId', options.collectionId);
    }
    if (options?.userGroupIds && options.userGroupIds.length > 0) {
      formData.append('userGroupIds', JSON.stringify(options.userGroupIds));
    }
    if (options?.channelIds && options.channelIds.length > 0) {
      formData.append('channelIds', JSON.stringify(options.channelIds));
    }
    if (options?.extractImages !== undefined) {
      formData.append('extractImages', options.extractImages.toString());
    }
    if (options?.strategy) {
      formData.append('strategy', options.strategy);
    }
    if (options?.vlmModelProvider) {
      formData.append('vlmModelProvider', options.vlmModelProvider);
    }

    return await this.apiService.postPromise<{
      article: KnowledgeBaseArticle;
      imageCount: number;
      created: boolean;
      sourceUrl: string;
    }>('/knowledge-base/import-document', formData);
  }
}
